import { useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';

import { FieldStateKey, NO_KEY, PermissionKey, YES_KEY } from '@/common/constants';
import { AllowAssumedContribution } from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/AccountUsage.types';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

interface Props {
  formattedData?: EmployerDto;
  isLoading?: boolean;
}

const useAdminContributionsFields = (props: Props): Field[] => {
  const {
    formattedData,
    isLoading,
  } = props;
  const { viewMode } = useSetupEmployerMode();

  const allowAdminContributionPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ALLOW_MANUAL_CONTRIBUTION,
    FieldStateKey.ALLOW_MANUAL_CONTRIBUTION
  );
  const allowScheduledContributionPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ALLOW_SCHEDULED_CONTRIBUTION,
    FieldStateKey.ALLOW_SCHEDULED_CONTRIBUTION
  );

  const fields =  useMemo(() => {
    if (isLoading) return [];

    return [
      {
        name: 'allowAdminContribution',
        type: FieldTypes.Radio,
        disabled: viewMode,
        label: 'Allow manual contributions',
        hasHorizontalLine: true,
        options: [
          { label: 'Yes', value: YES_KEY },
          { label: 'No', value: NO_KEY },
        ],
        ...(typeof formattedData?.overridableProperties?.allowAdminContribution === 'boolean' ? {
          defaultValue: formattedData?.overridableProperties?.allowAdminContribution ? YES_KEY : NO_KEY,
        } : {}),
        ...allowAdminContributionPermProps,
      },
      {
        name: 'allowAssumedContribution',
        type: FieldTypes.Radio,
        disabled: viewMode,
        label: 'Allow scheduled contributions',
        hasHorizontalLine: true,
        options: [
          { label: 'Allow with auto approval', value: AllowAssumedContribution.ALLOWED_WITH_AUTO_APPROVAL },
          { label: 'Allow with manual approval', value: AllowAssumedContribution.ALLOWED_WITH_MANUAL_APPROVAL },
          { label: 'Disable', value: AllowAssumedContribution.DISABLED },
        ],
        ...(formattedData?.overridableProperties?.allowAssumedContribution ? {
          defaultValue: formattedData?.overridableProperties?.allowAssumedContribution,
        } : {}),
        ...allowScheduledContributionPermProps,
      },
    ] as Field[];
   
  }, [
    viewMode,
    formattedData,
    isLoading,
    allowAdminContributionPermProps,
    allowScheduledContributionPermProps,
  ]);
  const formattedFields = useHideFields(fields);
  return formattedFields;
};

export default useAdminContributionsFields;
